"use client";

import type { DestinationsUi } from "@/lib/content/pages/destinations";
import type { Destination, Region } from "@/lib/destinations/types";
import { DESTINATIONS_BY_REGION_ORDER } from "@/lib/destinations/data";

type Props = {
  destinations: Destination[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  locale: "fr" | "en";
  ui: DestinationsUi;
};

export default function DestinationsList({
  destinations,
  selectedId,
  onSelect,
  locale,
  ui,
}: Props) {
  const regionLabels: Record<Region, string> = {
    Africa: ui.regionAfrica,
    Europe: ui.regionEurope,
    "Middle East": ui.regionMiddleEast,
    Asia: ui.regionAsia,
    Americas: ui.regionAmericas,
  };
  // Grouper par région dans l'ordre voulu
  const grouped = DESTINATIONS_BY_REGION_ORDER.map((region) => ({
    region,
    items: destinations
      .filter((d) => d.region === region)
      .sort((a, b) => a.city.localeCompare(b.city, locale)),
  })).filter((g) => g.items.length > 0);

  return (
    <section className="border-t border-dashed border-white/15 bg-[#0b0b0d] py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-body">
            {ui.listEyebrow}
          </p>
          <h2 className="mt-6 font-heading text-4xl md:text-5xl leading-[1.1] font-light text-white">
            {ui.listTitle}
          </h2>
        </div>

        <div className="mt-16 space-y-16">
          {grouped.map(({ region, items }) => (
            <div key={region}>
              <h3 className="text-[11px] tracking-[0.32em] uppercase text-white/55 font-body border-b border-white/10 pb-4">
                {regionLabels[region]}
                <span className="ml-3 text-white/30">— {items.length}</span>
              </h3>
              <ul className="mt-8 grid grid-cols-1 gap-x-12 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((d) => {
                  const isSelected = d.id === selectedId;
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(d.id)}
                        className={[
                          "group flex w-full items-baseline justify-between gap-4 border-b border-white/5 py-3 text-left transition",
                          isSelected ? "border-white/30" : "hover:border-white/15",
                        ].join(" ")}
                        aria-pressed={isSelected}
                      >
                        <span className="font-heading text-xl font-light text-white">
                          {d.city}
                        </span>
                        <span className="text-[10px] tracking-[0.28em] uppercase text-white/40 font-body group-hover:text-white/65 transition">
                          {d.country[locale]} →
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
